import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState, type ReactNode } from 'react';

const INSET = 22;

/**
 * A scrolling panel without the system scroll bar. Content scrolls with the wheel, trackpad, touch or keyboard as
 * usual; a slim indicator fades in while scrolling or on hover (and can be dragged), like on Apple devices.
 * Soft fades at the top and bottom show there's more to see.
 */
export const ScrollArea = forwardRef<HTMLDivElement, { children: ReactNode; className?: string; innerClassName?: string; style?: React.CSSProperties; label?: string; as?: 'section' | 'div' }>(
  function ScrollArea({ children, className, innerClassName, style, label, as = 'div' }, ref) {
    const inner = useRef<HTMLDivElement>(null);
    useImperativeHandle(ref, () => inner.current!);
    const [bar, setBar] = useState({ top: 0, size: 0, show: false, atTop: true, atEnd: true });
    const hide = useRef<ReturnType<typeof setTimeout>>(undefined);

    const measure = useCallback((reveal: boolean) => {
      const el = inner.current;
      if (!el) return;
      const { scrollTop, scrollHeight, clientHeight } = el;
      const scrollable = scrollHeight > clientHeight + 1;
      // Kept clear of rounded corners.
      const size = scrollable ? Math.max(28, (clientHeight / scrollHeight) * (clientHeight - 2 * INSET)) : 0;
      const top = scrollable ? INSET + (scrollTop / (scrollHeight - clientHeight)) * (clientHeight - 2 * INSET - size) : 0;
      setBar((b) => ({ top, size, show: reveal ? scrollable : b.show && scrollable, atTop: scrollTop <= 1, atEnd: scrollTop + clientHeight >= scrollHeight - 1 }));
      if (reveal) { clearTimeout(hide.current); hide.current = setTimeout(() => setBar((b) => ({ ...b, show: false })), 900); }
    }, []);

    useEffect(() => {
      const el = inner.current!;
      const ro = new ResizeObserver(() => measure(false));
      ro.observe(el);
      for (const c of el.children) ro.observe(c);
      const mo = new MutationObserver(() => { for (const c of el.children) ro.observe(c); measure(false); });
      mo.observe(el, { childList: true });
      measure(false);
      return () => { ro.disconnect(); mo.disconnect(); clearTimeout(hide.current); };
    }, [measure]);

    const drag = useRef<{ y: number; st: number } | null>(null);
    const Tag = as;
    return (
      <Tag className={`scroll-area${className ? ' ' + className : ''}${bar.atTop ? '' : ' fade-top'}${bar.atEnd ? '' : ' fade-end'}`} style={style} aria-label={label}>
        <div ref={inner} className={`scroll-inner${innerClassName ? ' ' + innerClassName : ''}`} onScroll={() => measure(true)} tabIndex={-1}>
          {children}
        </div>
        {bar.size > 0 && (
          <div className={`scroll-thumb${bar.show || drag.current ? ' on' : ''}`} style={{ height: bar.size, transform: `translateY(${bar.top}px)` }} aria-hidden
            onPointerDown={(e) => { e.preventDefault(); (e.target as HTMLElement).setPointerCapture(e.pointerId); drag.current = { y: e.clientY, st: inner.current!.scrollTop }; }}
            onPointerMove={(e) => {
              const d = drag.current, el = inner.current;
              if (!d || !el) return;
              const ratio = (el.scrollHeight - el.clientHeight) / Math.max(1, el.clientHeight - 2 * INSET - bar.size);
              el.scrollTop = d.st + (e.clientY - d.y) * ratio;
            }}
            onPointerUp={() => { drag.current = null; measure(true); }} onPointerCancel={() => { drag.current = null; }} />
        )}
      </Tag>
    );
  },
);
